"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, Mail, MapPin, Phone, Send } from "lucide-react";
export default function Contact() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contact" ref={sectionRef} className="dark bg-background text-foreground py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <div className={`text-center mb-12 transition-all duration-[var(--duration-medium)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          <h2 className="font-display font-extrabold text-4xl mb-4 !text-[hsl(35,88%,47%)] md:text-5xl">
            Contact
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            We are ready to help solve your legal problems professionally and with integrity
          </p>
        </div>

        <FormProvider className="grid grid-cols-1 lg:grid-cols-2 gap-12" formId="contact_form_home" sectionName="contact-section" defaultValues={{
        full_name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      }} validationRules={{
        full_name: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: false
        },
        subject: {
          required: true,
          minLength: 2,
          maxLength: 200
        },
        message: {
          required: true,
          minLength: 10
        }
      }} submitText="Send Message" successMessage="Thank you for contacting us! We will get back to you soon." errorMessage="Failed to send message. Please try again.">
          <ContactContent sectionVisible={sectionVisible} />
        </FormProvider>
      </div>
    </section>;
}
function ContactContent({
  sectionVisible
}: {
  sectionVisible: boolean;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const contactInfo = [{
    icon: MapPin,
    label: "Office Address",
    value: "Jakarta, Indonesia"
  }, {
    icon: Phone,
    label: "WhatsApp",
    value: "+62 823-1033-5547"
  }, {
    icon: Mail,
    label: "Email",
    value: "info@fakteintegritas.com"
  }];
  if (isSubmitted && !submitError) {
    return <div className="lg:col-span-2">
        <Card className="bg-secondary border border-primary/30 p-8 text-center">
          <CardContent>
            <div className="mb-6">
              <div className="size-16 rounded-full bg-primary/20 mx-auto flex items-center justify-center mb-4">
                <Send className="size-8 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2">Message Sent!</h3>
              <p className="text-muted-foreground text-lg">{successMessage}</p>
            </div>
            <Button onClick={resetForm} className="btn-primary">
              Send Another Message
            </Button>
          </CardContent>
        </Card>
      </div>;
  }
  return <>
      <div className={`space-y-6 transition-all duration-[var(--duration-medium)] delay-100 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        <h3 className="font-display font-bold text-foreground text-3xl mb-6">
          Contact Information
        </h3>
        {contactInfo.map((item, i) => {
        const Icon = item.icon;
        return <Card key={i} data-index={i} className="bg-secondary border border-border hover:border-primary/50 transition-all duration-[var(--duration-medium)] p-6">
              <CardContent className="flex items-start gap-4 p-0">
                <div className="size-12 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon className="size-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground text-lg mb-1">
                    {item.label}
                  </h4>
                  <p className="text-muted-foreground">{item.value}</p>
                </div>
              </CardContent>
            </Card>;
      })}
      </div>

      <div className={`transition-all duration-[var(--duration-medium)] delay-200 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        <h3 className="font-display font-bold text-foreground text-3xl mb-6">
          Send Message
        </h3>
        <Form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <FormField control={control} name="full_name" rules={{
            required: "Full name is required",
            minLength: {
              value: 2,
              message: "Name must be at least 2 characters"
            },
            maxLength: {
              value: 100,
              message: "Name must not exceed 100 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" className="bg-secondary border-muted focus:border-primary" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john@example.com" className="bg-secondary border-muted focus:border-primary" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            pattern: {
              value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
              message: "Invalid phone number"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+62 812-3456-7890" className="bg-secondary border-muted focus:border-primary" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="subject" rules={{
            required: "Subject is required",
            minLength: {
              value: 2,
              message: "Subject must be at least 2 characters"
            },
            maxLength: {
              value: 200,
              message: "Subject must not exceed 200 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Subject</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Consultation Request" className="bg-secondary border-muted focus:border-primary" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="message" rules={{
            required: "Message is required",
            minLength: {
              value: 10,
              message: "Message must be at least 10 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <textarea placeholder="Please provide details of your inquiry..." className="flex min-h-[120px] w-full rounded-md border border-muted bg-secondary px-3 py-2 text-base shadow-xs placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {submitError && <div className="p-4 bg-red-900/20 border border-red-500/50 rounded-md">
                <p className="text-red-400 text-sm">{submitError}</p>
              </div>}

            <Button type="submit" disabled={isSubmitting} className="btn-primary w-full group">
              {isSubmitting ? <>
                  <Loader className="animate-spin size-5" />
                  Sending...
                </> : <>
                  Send Message
                  <Send className="size-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-[var(--duration-fast)]" />
                </>}
            </Button>
          </div>
        </Form>
      </div>
    </>;
}